import '/assets/styles/pages/contact.scss';
import { useState, useEffect, useMemo } from 'react';

import config from '@wearetla/tla-essentials-tools/config';

// Functions
import formatPhone from '@wearetla/tla-essentials-tools/functions/format-phone';

// Templates
import { SidemenuTemplate } from '/views/layouts/sidemenu';

// Partials
import { Input, Form, FormFeedback } from '/views/partials/forms';
import Icon from '@wearetla/tla-essentials-tools/partials/icon';
import Btn from '/views/partials/btn';
import MapElement from '/views/partials/mapelement';
import Turnstile from '/views/partials/turnstile';

// Sections
import SectionHeading from '/views/sections/sidemenu/section-heading';
import Section from '/views/sections/sidemenu/section';

// Context
import { useNavigator, useParams } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useHead } from '@wearetla/tla-essentials-tools/utilities/head';
import { useBreakpoints } from '@wearetla/tla-essentials-tools/utilities/breakpoints';
import { useTranslation, createI18nInstance } from '/utilities/i18n';

// Services
import siteServices from '/services/site';
import useBranches from '/hooks/branches';

const parseHead = (activeBranch, t) => {
	return {
		title: activeBranch
			? t('{{branchTitle}} İletişim Bilgileri', { branchTitle: activeBranch.title })
			: t('İletişim'),
		description: t('{{brandName}} şube iletişim bilgileri.', { brandName: config.brandInfo.name }),
	};
};

const getBranches = () => {
	return siteServices.getAllBranches();
};

const parseActiveBranch = (branchData, branchSlug) => {
	const activeBranch = (branchData ?? []).find((b) => b.slug === branchSlug) ?? branchData?.[0];

	if (activeBranch) {
		return {
			...activeBranch,
			formattedPhone: formatPhone(activeBranch.phone),
		};
	}

	return null;
};

const Contact = () => {
	const { t } = useTranslation();
	const { desktop } = useBreakpoints();
	const { setHead } = useHead();

	const [showMap, setShowMap] = useState(false);
	const branchData = useBranches();

	const { redirect } = useNavigator();
	const { branch: branchSlug } = useParams(['contact']);

	const activeBranch = useMemo(() => {
		return parseActiveBranch(branchData, branchSlug);
	}, [branchData, branchSlug]);

	const branchNavItems = useMemo(
		() =>
			(branchData ?? []).map((b) => ({
				title: b.title,
				href: 'contact',
				active: b.slug === activeBranch?.slug,
				params: { branch: b.slug },
			})),
		[activeBranch, branchData]
	);

	useEffect(() => {
		if (!branchSlug && activeBranch) {
			redirect('contact', { branch: activeBranch.slug });
		} else {
			setShowMap(false);
			setHead(parseHead(activeBranch, t));
		}
	}, [activeBranch, branchSlug, redirect, t]);

	return (
		<SidemenuTemplate title={t('İletişim')} menuItems={branchNavItems}>
			<Section loading={!activeBranch}>
				{activeBranch && (
					<>
						{desktop && <SectionHeading lined={false} title={activeBranch.title} />}

						<div className="contact-info">
							<div className="info-section">
								<Icon name="address" /> {activeBranch.address}
							</div>
							<div className="info-section">
								<Icon name="phone" />{' '}
								<a href={`tel:${activeBranch.phone}`}>{activeBranch.formattedPhone}</a>
							</div>
							<div className="info-section">
								<Icon name="envelope-dark" />{' '}
								<a href={`mailto:${activeBranch.email}`}>{activeBranch.email}</a>
							</div>

							{showMap && (
								<MapElement
									className="info-map"
									center={{
										lat: activeBranch.lat,
										lng: activeBranch.lng,
									}}
									zoom={16}
									markers={[
										{
											position: {
												lat: activeBranch.lat,
												lng: activeBranch.lng,
											},
										},
									]}
								></MapElement>
							)}

							<Btn
								onClick={() => {
									setShowMap(!showMap);
								}}
								icon="map"
								afterIcon={showMap ? 'angle-up' : 'angle-down'}
								className="info-mapbtn primary"
							>
								{showMap ? t('Haritayı Gizle') : t('Haritada Gör')}
							</Btn>
						</div>
					</>
				)}
			</Section>

			<ContactForm activeBranch={activeBranch} branchData={branchData} />
		</SidemenuTemplate>
	);
};

const ContactForm = ({ branchData, activeBranch }) => {
	const { t } = useTranslation();
	const [feedbackData, setFeedbackData] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);
	const [contactSubjects, setContactSubjects] = useState(null);

	const { branchOptions, selectedBranch } = useMemo(() => {
		const branchOptions = (branchData ?? []).map((branch) => ({
			...branch,
			value: branch.id,
			label: branch.title,
		}));

		return {
			branchOptions,
			selectedBranch: branchOptions.find((b) => b.id === activeBranch?.id) ?? null,
		};
	}, [branchData, activeBranch]);

	useEffect(() => {
		setContactSubjects(null);
		setFeedbackData(null);
		setSuccess(false);

		if (activeBranch) {
			siteServices
				.getContactSubjects(activeBranch.id)
				.then(setContactSubjects)
				.catch(setFeedbackData);
		}
	}, [activeBranch]);

	const submit = (formFields) => {
		setFeedbackData(null);
		setSubmitting(true);

		siteServices
			.submitContactForm(formFields)
			.then((payload) => {
				setFeedbackData(payload);
				setSuccess(true);
				setSubmitting(false);
			})
			.catch((error) => {
				setFeedbackData(error);
				setSubmitting(false);
			});
	};

	return (
		<Section>
			<SectionHeading lined={false} title={t('İletişim Formu')} />

			<Form onSubmit={submit} className="contact-contactform grid-container">
				{!success && (
					<div className="row">
						<div className="col">
							{t('Formu doldurarak bize ulaş, en kısa zamanda sana geri dönelim ;)')}
						</div>
					</div>
				)}
				<FormFeedback inGrid data={feedbackData} />
				{!success && (
					<>
						<div className="row">
							<div className="col x6">
								<Input
									disabled={submitting || !contactSubjects}
									type="select"
									z={2}
									name="type"
									label={t('Konu')}
									options={contactSubjects}
									value={contactSubjects?.[0]}
									validation={{
										required: t('Bir konu seçmelisiniz.'),
									}}
								/>
							</div>
							<div className="col x6">
								<Input
									disabled={!!selectedBranch}
									type="select"
									z={1}
									name="branch_id"
									label={t('Hangi Şube?')}
									options={branchOptions}
									value={selectedBranch}
									validation={{
										required: t('Bir şube seçmelisiniz.'),
									}}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col x6">
								<Input
									disabled={submitting}
									type="text"
									name="name"
									label={t('Ad')}
									autoComplete="given-name"
									validation={{
										required: t('Adınızı girmelisiniz.'),
									}}
								/>
							</div>
							<div className="col x6">
								<Input
									disabled={submitting}
									type="text"
									name="surname"
									label={t('Soyad')}
									autoComplete="family-name"
									validation={{
										required: t('Soyadınızı girmelisiniz.'),
									}}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col x6">
								<Input
									disabled={submitting}
									type="text"
									name="email"
									label={t('E-Posta')}
									autoComplete="email"
									validation={{
										required: t('E-Posta adresinizi girmelisiniz.'),
										email: true,
									}}
								/>
							</div>
							<div className="col x6">
								<Input
									disabled={submitting}
									type="phone"
									label={t('Telefon')}
									name="phone"
									autoComplete="tel"
									validation={{
										required: t('Telefon numaranızı girmelisiniz.'),
										phone: t('Geçerli bir telefon numarası girmelisiniz.'),
									}}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col">
								<Input
									disabled={submitting}
									type="textarea"
									name="message"
									label={t('Neler oluyor?')}
									validation={{
										required: t('Mesajınızı girmelisiniz.'),
										minLength: [t('Mesajınız en az {length} karakter içermelidir.'), 10],
									}}
								/>
							</div>
						</div>
						{!!config.turnstile?.siteKey && (
							<div className="row">
								<div className="col x4">
									<Turnstile id="turnstile-contact" />
								</div>
							</div>
						)}
						<div className="row">
							<div className="col x6">
								<Btn
									type="submit"
									disabled={submitting}
									loading={submitting}
									className="form-submitbtn primary big"
								>
									{t('Gönder')}
								</Btn>
							</div>
						</div>
					</>
				)}

				{success && (
					<div className="row">
						<div className="col al-center">
							<Btn
								onClick={() => {
									setSuccess(false);
									setFeedbackData(null);
								}}
								className="primary"
							>
								{t('Yeniden Gönder')}
							</Btn>
						</div>
					</div>
				)}
			</Form>
		</Section>
	);
};

Contact.getServerProps = ({ match }) => {
	const t = createI18nInstance().t;

	return new Promise((resolve) => {
		Promise.all([getBranches()])
			.then(([branches]) => {
				const activeBranch = parseActiveBranch(branches, match.params.branch);

				resolve({
					sharedData: {
						branches,
					},
					head: parseHead(activeBranch, t),
				});
			})
			.catch(() => {
				resolve({});
			});
	});
};

export default Contact;